import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Button, EmptyState, Page, Table, Text, type Column } from "@marekl/adminer/components";
import { useConfirm, useTableSort } from "@marekl/adminer/hooks";
import { formatDateTime } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import { useAudienceLabel } from "../../../components/AudienceFields";
import { RowLink } from "../../../components/RowLink";
import {
  useAllCustomPages,
  useDeleteCustomPage,
  type CustomPage,
} from "../../../lib/queries/customPages";
import { customPagePath } from "../../../lib/customPages";
import { readsOnlyAsGlobalViewer, useMe } from "../../../auth/queries";

export function CustomPagesPage() {
  const { data: me } = useMe();
  // Čtení všeho reads every page, drafts included, as it renders: the title
  // opens the page itself, never the editor.
  const readOnly = readsOnlyAsGlobalViewer(me, "custom_pages_manager");
  const { data: pages = [], isLoading, error, refetch } = useAllCustomPages();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { mutateAsync: deletePage } = useDeleteCustomPage();
  const targetsLabel = useAudienceLabel();

  const audienceLabel = useCallback(
    (p: CustomPage) => targetsLabel(p) || "všichni přihlášení",
    [targetsLabel],
  );

  const askDelete = useCallback(
    (p: CustomPage) =>
      confirm({
        title: "Smazat stránku",
        variant: "danger",
        description: (
          <>
            Smazat stránku <strong>{p.title}</strong>? Zmizí všem, kdo ji vidí, i s obsahem.
          </>
        ),
        confirmLabel: "Smazat",
        onConfirm: async () => {
          await deletePage(p.id);
        },
      }),
    [confirm, deletePage],
  );

  const columns = useMemo<Column<CustomPage>[]>(
    () => [
      {
        key: "title",
        header: "Název",
        primary: true,
        sortable: true,
        sortValue: (p) => p.title,
        render: (p) => (
          <>
            <div>
              <RowLink to={readOnly ? customPagePath(p.slug) : `/admin/stranky/${p.id}`}>
                {p.title}
              </RowLink>
            </div>
            <Text variant="muted">{customPagePath(p.slug)}</Text>
          </>
        ),
      },
      {
        key: "audience",
        header: "Komu",
        sortable: true,
        sortValue: (p) => audienceLabel(p),
        render: (p) => audienceLabel(p),
      },
      {
        key: "status",
        header: "Stav",
        sortable: true,
        sortValue: (p) => (p.published ? "Zveřejněno" : "Nezveřejněno"),
        render: (p) =>
          p.published ? (
            <Badge variant="ok">Zveřejněno</Badge>
          ) : (
            <Badge variant="muted">Nezveřejněno</Badge>
          ),
      },
      {
        key: "order",
        header: "Pořadí",
        sortable: true,
        sortValue: (p) => p.sort_order,
        render: (p) => p.sort_order,
      },
      {
        key: "updated",
        header: "Upraveno",
        sortable: true,
        sortValue: (p) => p.updated_at.toISOString(),
        render: (p) => <Text variant="muted">{formatDateTime(p.updated_at)}</Text>,
      },
      {
        key: "actions",
        header: "",
        render: (p) => (
          <Table.Actions>
            <Table.Action appearance="button" onClick={() => navigate(customPagePath(p.slug))}>
              Zobrazit
            </Table.Action>
            {!readOnly && (
              <>
                <Table.Action appearance="button" onClick={() => navigate(`/admin/stranky/${p.id}`)}>
                  Upravit
                </Table.Action>
                <Table.Action appearance="button" variant="danger" onClick={() => askDelete(p)}>
                  Smazat
                </Table.Action>
              </>
            )}
          </Table.Actions>
        ),
      },
    ],
    [audienceLabel, askDelete, navigate, readOnly],
  );

  const tableSort = useTableSort(pages, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "order", dir: "asc" },
  });

  return (
    <>
      <AppTopbar crumbs={[{ label: "Stránky" }]} />
      <Page>
        <Page.Header
          title="Stránky"
          subtitle="vlastní stránky pro vybrané role, týmy a federace"
          actions={
            !readOnly && (
              <Button variant="primary" onClick={() => navigate("/admin/stranky/nova")}>
                Nová stránka
              </Button>
            )
          }
        />
        <Page.Content stack gap="lg">
          <Table<CustomPage>
            framed
            {...tableSort.tableProps}
            rowKey={(p) => p.id}
            columns={columns}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            cards="sm"
            empty={
              <EmptyState
                title="Žádné stránky"
                description="Zatím tu není žádná stránka. Založte první tlačítkem „Nová stránka“."
              />
            }
          />
        </Page.Content>
      </Page>
    </>
  );
}
